import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    TablePagination,
    Alert,
    CircularProgress,
    SelectChangeEvent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { format, startOfMonth, endOfMonth, parseISO, startOfWeek, endOfWeek, subDays, subMonths } from 'date-fns';
import { profileModel, teleConsultationModel } from '../Axios/AxiosConfig';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

interface Appointment {
    Appointment_id?: string;
    id?: string;
    UID?: string;
    Invoice?: any;
    appointment_time?: string;
    doctormessage?: string;
    appointment_status?: string;
    doctor_photo?: string;
    appointment_date?: string;
    consultation_type?: string;
    payment_status?: string;
    discount?: number;
    patient_age?: string;
    history_appointment_time?: string;
    doctor_status?: string;
    doctor_id?: string;
    doctor_uid?: string;
    doctor_speciality?: string;
    history_appointment_date?: string;
    consultation_reason?: string;
    Paid?: number;
    Doctor_Signature?: string;
    appointmentID?: string;
    patient_id?: string;
    patient_name?: string;
    doctor_name?: string;
    timestamp?: string;
    isClinicalUser?: boolean;
    txnid?: string;
    consultation_status?: string;
}

interface Analytics {
    totalAppointments: number;
    completedAppointments: number;
    cancelledAppointments: number;
    upcomingAppointments: number;
    clinicalUsers: number;
    regularUsers: number;
    totalCharges: number;
    averageCharge: number;
}

const ConsultationsPage: React.FC = () => {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
    const [analytics, setAnalytics] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
    const [doctorFilter, setDoctorFilter] = useState<string>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
    const [openDateRangeDialog, setOpenDateRangeDialog] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<{ startDate: any; endDate: any }>({
        startDate: '',
        endDate: ''
    });

    const dateRangePresets = [
        {
            label: 'Today',
            getRange: () => {
                const today = new Date();
                return {
                    startDate: format(today, 'yyyy-MM-dd'),
                    endDate: format(today, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'Yesterday',
            getRange: () => {
                const yesterday = subDays(new Date(), 1);
                return {
                    startDate: format(yesterday, 'yyyy-MM-dd'),
                    endDate: format(yesterday, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'This Week',
            getRange: () => {
                const today = new Date();
                return {
                    startDate: format(startOfWeek(today), 'yyyy-MM-dd'),
                    endDate: format(today, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'Last Week',
            getRange: () => {
                const today = new Date();
                const lastWeek = subDays(today, 7);
                return {
                    startDate: format(startOfWeek(lastWeek), 'yyyy-MM-dd'),
                    endDate: format(endOfWeek(lastWeek), 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'This Month',
            getRange: () => {
                const today = new Date();
                return {
                    startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
                    endDate: format(today, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'Last Month',
            getRange: () => {
                const today = new Date();
                const firstDayLastMonth = startOfMonth(subMonths(today, 1));
                const lastDayLastMonth = endOfMonth(subMonths(today, 1));
                return {
                    startDate: format(firstDayLastMonth, 'yyyy-MM-dd'),
                    endDate: format(lastDayLastMonth, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'Last 3 Months',
            getRange: () => {
                const today = new Date();
                const threeMonthsAgo = subMonths(today, 3);
                return {
                    startDate: format(threeMonthsAgo, 'yyyy-MM-dd'),
                    endDate: format(today, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'Last 6 Months',
            getRange: () => {
                const today = new Date();
                const sixMonthsAgo = subMonths(today, 6);
                return {
                    startDate: format(sixMonthsAgo, 'yyyy-MM-dd'),
                    endDate: format(today, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'This Year',
            getRange: () => {
                const today = new Date();
                return {
                    startDate: format(new Date(today.getFullYear(), 0, 1), 'yyyy-MM-dd'),
                    endDate: format(today, 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'Last Year',
            getRange: () => {
                const lastYear = new Date().getFullYear() - 1;
                return {
                    startDate: format(new Date(lastYear, 0, 1), 'yyyy-MM-dd'),
                    endDate: format(new Date(lastYear, 11, 31), 'yyyy-MM-dd')
                };
            }
        },
        {
            label: 'Get All',
            getRange: () => ({
                startDate: undefined,
                endDate: undefined
            })
        }
    ];

    const getCurrentMonthRange = () => {
        const now = new Date();
        const currentStartDate = format(startOfMonth(now), 'yyyy-MM-dd');
        const currentEndDate = format(endOfMonth(now), 'yyyy-MM-dd');
        return { startDate: currentStartDate, endDate: currentEndDate };
    };

    const fetchUserProfile = async (userId: string, apt: Appointment) => {
        try {

            const response = await profileModel.get(`${process.env.REACT_APP_CLINICAL_URL}/Profile/${userId}`);
            if (response.status === 200 && response.data) {
                const { firstname, lastname } = response.data;

                apt.patient_name = firstname + ' ' + lastname;

                setAppointments((prevAppointments) =>
                    prevAppointments.map((apt) =>
                        apt.patient_id === userId ? { ...apt, patient_name: firstname + ' ' + lastname } : apt
                    )
                );

            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
        }
    };


    const fetchAppointments = async (startDate?: string, endDate?: string, getAll: boolean = false) => {
        try {
            setLoading(true);
            setError(null);

            const params: any = {};

            if (!getAll && startDate && endDate) {
                params.startDate = startDate;
                params.endDate = endDate;
            }

            const response = await teleConsultationModel.get('/getAllAppointments', { params });

            if (response.data) {

                setAppointments(response.data);
                setFilteredAppointments(response.data);
                calculateAnalytics(response.data);

                for (const apt of response.data) {
                    if (apt.patient_name === "undefined undefined") {
                        fetchUserProfile(apt.patient_id, apt);
                    }
                }


            }
        } catch (err: any) {
            console.error('Error fetching appointments:', err);
            setError(err.response?.data?.error || 'Failed to fetch appointments');
        } finally {
            setLoading(false);
        }
    };

    const handleClearDateRange = () => {
        setStartDate('');
        setEndDate('');
        setDateRange({ startDate: '', endDate: '' });
        applyFilters(searchTerm, statusFilter, typeFilter, doctorFilter, '', '', userTypeFilter);
    };

    const handleOpenDateRangeDialog = () => {
        setOpenDateRangeDialog(true);
    };

    const handleCloseDateRangeDialog = () => {
        setOpenDateRangeDialog(false);
    };

    const handleDateRangeChange = (range: { startDate: string; endDate: string }) => {
        setDateRange(range);
        if (range.startDate != '' && range.endDate != '') {
            setStartDate(range.startDate);
            setEndDate(range.endDate);
            applyFilters(searchTerm, statusFilter, typeFilter, doctorFilter, range.startDate, range.endDate, userTypeFilter);

            fetchAppointments(range.startDate, range.endDate, false);
        }

    };

    const calculateAnalytics = (appointmentData: Appointment[]) => {
        const totalAppointments = appointmentData.length;
        const completedAppointments = appointmentData.filter(apt =>
            apt.appointment_status === 'completed' || apt.consultation_status === 'completed'
        ).length;
        const cancelledAppointments = appointmentData.filter(apt =>
            apt.appointment_status === 'cancelled' || apt.consultation_status === 'cancelled'
        ).length;

        const upcomingAppointments = appointmentData.filter(apt => {
            if (!apt.appointment_date) return false;
            const appointmentDate = parseISO(apt.appointment_date);
            const status = apt.appointment_status || apt.consultation_status;
            return appointmentDate > new Date() && status !== 'cancelled';
        }).length;


        const validCharges = appointmentData
            .map(apt => apt?.Paid || 0)
            .filter(charge => typeof charge === 'number' && charge > 0);
        const totalCharges = validCharges.reduce((sum, charge) => sum + charge, 0);
        const averageCharge = validCharges.length > 0 ? totalCharges / validCharges.length : 0;

        const clinicalUsers = appointmentData.filter((apt: any) => apt?.isClinicalUser).length;

        setAnalytics({
            totalAppointments,
            completedAppointments,
            cancelledAppointments,
            upcomingAppointments,
            clinicalUsers,
            totalCharges,
            averageCharge
        });

    };

    const applyFilters = (search: string, status: string, type: string, doctor: string, startDateFilter?: string, endDateFilter?: string, userType: string = 'all') => {
        let filtered = appointments;

        // Apply search filter
        if (search) {
            filtered = filtered.filter(appointment =>
                appointment.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
                appointment.patient_id?.toLowerCase().includes(search.toLowerCase()) ||
                appointment.consultation_type?.toLowerCase().includes(search.toLowerCase()) ||
                appointment.doctor_name?.toLowerCase().includes(search.toLowerCase())
            );
        }

        // Apply status filter
        if (status !== 'all') {
            filtered = filtered.filter(appointment => {
                const appointmentStatus = appointment.appointment_status || appointment.consultation_status;
                return appointmentStatus === status;
            });
        }

        // Apply type filter
        if (type !== 'all') {
            filtered = filtered.filter(appointment =>
                appointment.consultation_type === type
            );
        }

        // Apply doctor filter
        if (doctor !== 'all') {
            filtered = filtered.filter(appointment =>
                appointment.doctor_name === doctor
            );
        }

        // Apply user type filter
        if (userType !== 'all') {
            filtered = filtered.filter(appointment => {
                if (userType === 'clinical') {
                    return appointment.isClinicalUser === true;
                } else if (userType === 'commercial') {
                    return !appointment.isClinicalUser;
                }
                return true;
            });
        }

        // Apply date range filter
        if (startDateFilter || endDateFilter) {
            filtered = filtered.filter(appointment => {
                if (!appointment.appointment_date) return false;

                try {
                    // Handle different date formats
                    let appointmentDate: Date;

                    // Try parsing as ISO first
                    try {
                        appointmentDate = parseISO(appointment.appointment_date);
                    } catch (isoError) {
                        // If ISO parsing fails, try creating Date object directly
                        appointmentDate = new Date(appointment.appointment_date);
                    }

                    // If date is invalid, skip this appointment
                    if (isNaN(appointmentDate.getTime())) return false;

                    const start = startDateFilter ? new Date(startDateFilter) : null;
                    const end = endDateFilter ? new Date(endDateFilter) : null;

                    // If filter dates are invalid, skip filtering
                    if (start && isNaN(start.getTime())) return true;
                    if (end && isNaN(end.getTime())) return true;

                    // Compare dates (set time to start/end of day for proper comparison)
                    if (start) {
                        const startOfStartDate = new Date(start);
                        startOfStartDate.setHours(0, 0, 0, 0);
                        if (appointmentDate < startOfStartDate) return false;
                    }

                    if (end) {
                        const endOfEndDate = new Date(end);
                        endOfEndDate.setHours(23, 59, 59, 999);
                        if (appointmentDate > endOfEndDate) return false;
                    }

                    return true;
                } catch (error) {
                    console.error('Date filtering error for appointment:', appointment.appointment_date, error);
                    return false;
                }
            });
        }

        setFilteredAppointments(filtered);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
        applyFilters(value, statusFilter, typeFilter, doctorFilter, startDate, endDate, userTypeFilter);
    };

    const handleStatusFilter = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setStatusFilter(value);
        applyFilters(searchTerm, value, typeFilter, doctorFilter, startDate, endDate, userTypeFilter);
    };

    const handleTypeFilter = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setTypeFilter(value);
        applyFilters(searchTerm, statusFilter, value, doctorFilter, startDate, endDate, userTypeFilter);
    };

    const handleDoctorFilter = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setDoctorFilter(value);
        applyFilters(searchTerm, statusFilter, typeFilter, value, startDate, endDate, userTypeFilter);
    };

    const handleUserTypeFilter = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setUserTypeFilter(value);
        applyFilters(searchTerm, statusFilter, typeFilter, doctorFilter, startDate, endDate, value);
    };

    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setStartDate(value);
        applyFilters(searchTerm, statusFilter, typeFilter, doctorFilter, value, endDate);
    };

    const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setEndDate(value);
        applyFilters(searchTerm, statusFilter, typeFilter, doctorFilter, startDate, value);
    };

    const handleGetAllAppointments = () => {
        fetchAppointments(undefined, undefined, true);
    };

    const handleViewDetails = (appointment: Appointment) => {
        setSelectedAppointment(appointment);
        setDetailsOpen(true);
    };

    const handleCurrentMonth = () => {
        const { startDate, endDate } = getCurrentMonthRange();
        fetchAppointments(startDate, endDate, false);
    };

    const getStatusColor = (appointment: Appointment) => {
        const status = appointment?.appointment_status || appointment.consultation_status || '';
        switch (status) {
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            case 'confirmed': return 'primary';
            case 'upcoming': return 'warning';
            case 'rescheduled': return 'info';
            default: return 'default';
        }
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedAppointment(null);
    };

    const getUserTypeColor = (isClinicalUser?: boolean) => {
        return isClinicalUser ? 'secondary' : 'default';
    };

    useEffect(() => {
        const { startDate, endDate } = getCurrentMonthRange();
        fetchAppointments(startDate, endDate, false);
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, pt: 1.5 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontFamily: "Libre Baskerville", color: "#3B362A" }}>
                    Consultations
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    View and manage all patient consultations
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Analytics Cards */}
            {analytics && (
                <Grid
                    container
                    spacing={3}
                    sx={{
                        mb: 3,
                        "& .MuiCard-root": {
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        },
                    }}
                >

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            elevation={0}
                            sx={{
                                background: "linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)",
                                borderRadius: 3,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "12px",
                                            bgcolor: "primary.50",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 2,
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                        }}
                                    >
                                        <EventNoteIcon sx={{ color: "primary.main", fontSize: 26 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Appointments
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700} color="text.primary">
                                            {analytics.totalAppointments}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Completed */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            elevation={0}
                            sx={{
                                background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                                borderRadius: 3,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "12px",
                                            bgcolor: "success.50",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 2,
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                        }}
                                    >
                                        <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 26 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Completed
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700} color="success.main">
                                            {analytics.completedAppointments}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Clinical Users */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            elevation={0}
                            sx={{
                                background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                                borderRadius: 3,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "12px",
                                            bgcolor: "secondary.50",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 2,
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                        }}
                                    >
                                        <PeopleAltIcon sx={{ color: "secondary.main", fontSize: 26 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Clinical Users
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700} color="secondary.main">
                                            {analytics.clinicalUsers}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Total Revenue */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            elevation={0}
                            sx={{
                                background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                                borderRadius: 3,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "12px",
                                            bgcolor: "primary.50",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 2,
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                        }}
                                    >
                                        <CurrencyRupeeIcon sx={{ color: "primary.main", fontSize: 26 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Revenue
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ mt: 0.5 }}>
                                            ₹
                                            {analytics.totalCharges.toLocaleString("en-IN", {
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0,
                                            })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}


            {/* Controls */}
            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    '& .MuiCardContent-root': {
                        padding: 3
                    }
                }}
            >
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Search appointments"
                                value={searchTerm}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={statusFilter}
                                    label="Status"
                                    onChange={handleStatusFilter} sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'divider',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'primary.main',
                                        },
                                    }}>
                                    <MenuItem value="all">All Status</MenuItem>
                                    <MenuItem value="upcoming">Upcoming</MenuItem>
                                    <MenuItem value="confirmed">Confirmed</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleOpenDateRangeDialog}
                                sx={{
                                    height: '56px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    textTransform: 'none',
                                    borderColor: 'divider',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'action.hover'
                                    }
                                }}
                                startIcon={<EventNoteIcon />}
                            >
                                <Box sx={{ textAlign: 'left', flexGrow: 1 }}>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1 }}>
                                        Date Range
                                    </Typography>
                                    <Typography variant="body2">
                                        {dateRange.startDate && dateRange.endDate
                                            ? `${format(new Date(dateRange.startDate), 'MMM dd, yyyy')} - ${format(new Date(dateRange.endDate), 'MMM dd, yyyy')}`
                                            : 'Select date range'}
                                    </Typography>
                                </Box>
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <InputLabel>User Type</InputLabel>
                                <Select
                                    value={userTypeFilter}
                                    onChange={handleUserTypeFilter}
                                    label="User Type"
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'divider',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'primary.main',
                                        },
                                    }}
                                >
                                    <MenuItem value="all">All Users</MenuItem>
                                    <MenuItem value="clinical">Clinical Users</MenuItem>
                                    <MenuItem value="commercial">Commercial Users</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                    </Grid>
                </CardContent>
            </Card>

            {/* Appointments Table */}
            <Card
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    '& .MuiCardContent-root': {
                        p: 0
                    },
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                }}
            >
                <CardContent>
                    <TableContainer >
                        <Box sx={{
                            p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: '#efefe7',

                        }}>
                            <Typography variant="h6" sx={{ fontFamily: "Libre Baskerville", fontWeight: "700", color: "#3B362A" }} >
                                Appointments
                            </Typography>
                        </Box>
                        <Table>
                            <TableHead style={{ backgroundColor: '#f4ecd6', fontFamily: "'Poppins', sans-serif", fontWeight: "500", color: "#3B362A" }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Patient</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Time</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Doctor</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>User Type</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Consultation Fee</TableCell>
                                    <TableCell sx={{ fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody style={{ backgroundColor: '#f8f8f4', fontFamily: "'Poppins', sans-serif", fontWeight: 400, color: "#3B362A" }}>
                                {filteredAppointments
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((appointment) => (
                                        <TableRow key={appointment.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="'Poppins', sans-serif" fontWeight="400">
                                                    {appointment.patient_name || appointment.patient_id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 400, fontFamily: "'Poppins', sans-serif" }}>
                                                {appointment.appointment_date ? (() => {
                                                    try {
                                                        return format(parseISO(appointment.appointment_date!), 'MMM dd, yyyy');
                                                    } catch (error) {
                                                        return appointment.appointment_date;
                                                    }
                                                })() : 'N/A'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 400, fontFamily: "'Poppins', sans-serif" }}>
                                                {appointment.appointment_time || 'N/A'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 400, fontFamily: "'Poppins', sans-serif" }}>
                                                <Typography variant="body2" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 400 }}>
                                                    {appointment.doctor_name || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 400, fontFamily: "'Poppins', sans-serif" }}>
                                                <Chip
                                                    style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 400, fontSize: 12 }}
                                                    label={(appointment.appointment_status?.toUpperCase() || appointment.consultation_status?.toUpperCase()) || 'N/A'}
                                                    color={getStatusColor(appointment) as any}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 400, fontFamily: "'Poppins', sans-serif" }}>
                                                <Chip
                                                    style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 400 }}
                                                    label={appointment?.isClinicalUser ? "Clinical" : "Commercial"}
                                                    color={getUserTypeColor(appointment?.isClinicalUser)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 400, fontFamily: "'Poppins', sans-serif" }}>
                                                <Typography variant="body2" color="primary.main" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 400 }}>
                                                    ₹{Number(appointment.Paid || 0).toLocaleString('en-IN', {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 0
                                                    })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 400, fontFamily: "'Poppins', sans-serif" }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"

                                                    color="primary"

                                                    onClick={() => handleViewDetails(appointment)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 500,
                                                        borderRadius: 1,
                                                        px: 2,
                                                        py: 0.5,
                                                        fontFamily: "'Poppins', sans-serif",
                                                        fontSize: '0.8125rem',
                                                        '&:hover': {
                                                            backgroundColor: 'primary.light',
                                                            color: 'primary.dark',
                                                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
                                                        }
                                                    }}
                                                >
                                                    View Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', backgroundColor: '#efefe7' }}>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={filteredAppointments.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(parseInt(event.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Appointment Details Modal */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: "#f8f8f4",
                        fontFamily: '"Poppins", sans-serif !important',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        bgcolor: "#f4ecd6",
                        color: "#333",
                        borderBottom: "1px solid #efefe7",
                        fontFamily: '"Poppins", sans-serif !important',
                    }}
                >
                    Appointment Details
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDetails}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "#666",
                            "&:hover": { color: "#da6e0b" },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {selectedAppointment && (
                        <Grid container spacing={3}>
                            {/* Patient Information */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{ fontWeight: 600, color: "#da6e0b" }}
                                >
                                    Patient Information
                                </Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "#efefe7",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                    }}
                                >
                                    <Typography variant="body1">
                                        <strong>Name:</strong>{" "}
                                        {selectedAppointment.patient_name ||
                                            selectedAppointment.patient_id}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>User Type:</strong>{" "}
                                        {selectedAppointment.isClinicalUser ? "Clinical" : "Commercial"}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Appointment Details */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{ fontWeight: 600, color: "#da6e0b" }}
                                >
                                    Appointment Details
                                </Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "#efefe7",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                    }}
                                >
                                    <Typography variant="body1">
                                        <strong>Date:</strong>{" "}
                                        {selectedAppointment.appointment_date
                                            ? (() => {
                                                try {
                                                    return format(
                                                        parseISO(selectedAppointment.appointment_date),
                                                        "MMM dd, yyyy"
                                                    );
                                                } catch (error) {
                                                    return selectedAppointment.appointment_date;
                                                }
                                            })()
                                            : "N/A"}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Time:</strong> {selectedAppointment.appointment_time || "N/A"}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Doctor:</strong>{" "}
                                        {selectedAppointment.doctor_name || "N/A"}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Type:</strong>{" "}
                                        {selectedAppointment.consultation_type || "N/A"}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Status & Payment */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{ fontWeight: 600, color: "#da6e0b" }}
                                >
                                    Status & Payment
                                </Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "#efefe7",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                    }}
                                >
                                    <Typography variant="body1">
                                        <strong>Status:</strong>{" "}
                                        {selectedAppointment.appointment_status ||
                                            selectedAppointment.consultation_status ||
                                            "N/A"}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Consultation Fee:</strong> ₹{selectedAppointment.Paid || 0}
                                    </Typography>
                                    {selectedAppointment.txnid && (
                                        <Typography variant="body1">
                                            <strong>Transaction ID:</strong> {selectedAppointment.txnid}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>

                            {/* Additional Information */}
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="h6"
                                    gutterBottom
                                    sx={{ fontWeight: 600, color: "#da6e0b" }}
                                >
                                    Additional Information
                                </Typography>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "#efefe7",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                    }}
                                >
                                    {selectedAppointment.timestamp && (
                                        <Typography variant="body1">
                                            <strong>Created:</strong>{" "}
                                            {(() => {
                                                try {
                                                    return format(
                                                        parseISO(selectedAppointment.timestamp),
                                                        "MMM dd, yyyy HH:mm"
                                                    );
                                                } catch (error) {
                                                    return selectedAppointment.timestamp;
                                                }
                                            })()}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: "#f4ecd6" }}>
                    <Button
                        onClick={handleCloseDetails}
                        variant="contained"
                        sx={{
                            bgcolor: "#da6e0b",
                            "&:hover": { bgcolor: "#c25a05" },
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Date Range Picker Dialog */}
            <Dialog
                onClose={handleCloseDateRangeDialog}
                aria-labelledby="customized-dialog-title"
                open={openDateRangeDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                    Select Date Range
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleCloseDateRangeDialog}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Dialog
                    open={openDateRangeDialog}
                    onClose={handleCloseDateRangeDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            p: 2,
                            '& *': {
                                fontFamily: '"Poppins", sans-serif !important',
                            }
                        },
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 600, color: '#3B362A' }}>Select Date Range</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Start Date"
                                    value={dateRange.startDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="End Date"
                                    value={dateRange.endDate}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        min: dateRange.startDate
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#3B362A' }}>
                                    Quick Select:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {dateRangePresets.map((preset) => (
                                        <Button
                                            key={preset.label}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                const range = preset.getRange();
                                                setDateRange({
                                                    startDate: range.startDate,
                                                    endDate: range.endDate
                                                });
                                            }}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '0.7rem',
                                                py: 0.25,
                                                px: 1,
                                                minWidth: 'auto',
                                                borderRadius: 1,
                                                borderColor: '#e0e0e0',
                                                '&:hover': {
                                                    borderColor: '#3B362A',
                                                    backgroundColor: 'rgba(59, 54, 42, 0.04)'
                                                },
                                                color: '#3B362A',
                                                fontWeight: 400
                                            }}
                                        >
                                            {preset.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Button
                            onClick={handleCloseDateRangeDialog}
                            sx={{
                                textTransform: 'none',
                                color: '#3B362A',
                                '&:hover': {
                                    backgroundColor: 'rgba(59, 54, 42, 0.04)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                handleDateRangeChange(dateRange);
                                handleCloseDateRangeDialog();
                            }}
                            variant="contained"
                            disabled={dateRange.startDate === '' || dateRange.endDate === ''}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: '#3B362A',
                                '&:hover': {
                                    backgroundColor: '#2a2620'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                }
                            }}
                        >
                            Apply
                        </Button>
                    </DialogActions>
                </Dialog>
            </Dialog>
        </Box>
    );
};

export default ConsultationsPage;
